import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Grid, Paper, FormControl, InputLabel, Select, MenuItem, makeStyles, Typography, Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Button, styled, Tooltip, Checkbox, Dialog, DialogTitle, DialogContent, DialogActions } from '@material-ui/core';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import { getTeamAndFiscalQuarters, removeQuotaForReps, deleteQuotaRemoval, removeQuotaForRepsQuarterInfo, getQuotaTeamsThunk, deleteQuotaTeamThunk, getQuotaTeamsDrilldownThunk, getQuotaCompanyThunk, deleteQuotaCompanyThunk } from '../redux/management.thunk';
import { clearQuotaRemovalModalInfo, selectCurrentFiscalQuarter, selecTeamsRemoval, selectFiscalQuarters, selectQuotaRemovalModalInfo, selectTeamHierarchy } from '../redux/management.slice';
import { tooltipClasses } from '@mui/material';
import RepFilterPanel from './RepFilterPanel';
import RepTable from './RepTable';
import QuotaDetailsModal from './QuotaDetailsModal';

const useStyles = makeStyles((theme) => ({
  repTeamFilterContainer: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  formControl: {
    minWidth: 180,
  },
  filterLabel: {
    fontWeight: 500,
  },
  quotaRemovalHeader: {
    fontWeight: 700,
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  quotaRemovalSub: {
    marginBottom: theme.spacing(2),
    color: theme.palette.text.secondary,
  },
  tableHeader: {
    background: '#4472c4',
  },
  tableHeaderCell: {
    color: '#fff',
    fontWeight: 600,

  },
  searchBox: {
    marginBottom: theme.spacing(2),
    width: 350,
  },
  filterRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  tableRow: {
    '&:hover': {
      backgroundColor: '#f5f5f5',
    },
  },
  tableCell: {
    padding: theme.spacing(1),
  },
}));

const CustomWidthTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    minWidth: "none",
    fontSize: "1rem",
    color: "black",
    background: "white",
    fontWeight: 400,
    border: "0.5px solid lightgrey",
  },
});

// Main QuotaRemoval component
const QuotaRemoval = ({ mode = 'rep' }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const teamOptions = useSelector(selecTeamsRemoval);
  const teamHierarchy = useSelector(selectTeamHierarchy);
  const fiscalQuarterOptions = useSelector(selectFiscalQuarters);
  const [team, setTeam] = useState([]);
  const [fiscalQuarter, setFiscalQuarter] = useState('');
  const [showTable, setShowTable] = useState(false);
  const [search, setSearch] = useState('');
  const quotaReps = useSelector(state => state.management.removeQuotaMessage || []);
  const [selectedRep, setSelectedRep] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedQuarters, setSelectedQuarters] = useState([]);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const [repQuotaDetails, setRepQuotaDetails] = useState([]);
  const quotaRemovalModalInfo = useSelector(selectQuotaRemovalModalInfo);
  const currentFiscalQuarter = useSelector(selectCurrentFiscalQuarter)
  const [companyDeleteOpen, setCompanyDeleteOpen] = useState(false);

  const isTeamMode = mode === 'team';
  const isCompanyMode = mode === 'company';

  // Invoke getTeamAndFiscalQuarters when component loads
  useEffect(() => {
    dispatch(getTeamAndFiscalQuarters({ type: mode }));
  }, [dispatch, mode]);

  useEffect(() => {
    if (currentFiscalQuarter !== null) {
      setFiscalQuarter(currentFiscalQuarter)
    }
  }, []) //removed currentFiscalQuarter dependency

  useEffect(() => {
    if (isCompanyMode) {
      dispatch(getQuotaCompanyThunk({}));
      dispatch({ type: "info_message_generic", payload: { type: "info", message: "Loading Company Quotas" } })
      setShowTable(true);
    } else if (showTable && fiscalQuarter) {
      if (isTeamMode) {
        // Team Mode: Fetch teams with quota for the selected quarter
        // We don't need 'team' filter here as per requirement, just fiscalQuarter
        dispatch(getQuotaTeamsThunk({ fiscalQuarter }));
        dispatch({ type: "info_message_generic", payload: { type: "info", message: "Loading Quota Teams" } })
      } else if (team) {
        // Rep Mode: Existing logic
        // Helper function to flatten hierarchical team data
        const flattenTeamHierarchy = (hierarchy) => {
          const result = [];

          const flatten = (nodes) => {
            if (!nodes || !Array.isArray(nodes)) return;

            nodes.forEach(node => {
              // Skip the root "All" node, but add all other Parent values
              if (node.Parent && node.Parent !== "All") {
                result.push(node.Parent);
              }

              // Recursively flatten nested teams
              if (node.teams && node.teams.length > 0) {
                flatten(node.teams);
              }
            });
          };

          flatten(hierarchy);
          return result;
        };

        if (team.length === 1 && team[0] === "All") {
          // Flatten the team hierarchy and send all teams
          console.log(teamHierarchy, 'teamHierarchy');

          // Access the filters array from the nested structure
          const filters = teamHierarchy?.parentChildrenTeamFilter?.filters || [];
          const allTeams = flattenTeamHierarchy(filters);

          console.log(allTeams, 'flattened teams');

          dispatch(
            removeQuotaForReps({ team: allTeams, fiscalQuarter })
          );
          dispatch({ type: "info_message_generic", payload: { type: "info", message: "Loading Quota Reps" } })
        } else {
          dispatch(
            removeQuotaForReps({ team, fiscalQuarter })
          );
          dispatch({ type: "info_message_generic", payload: { type: "info", message: "Loading Quota Reps" } })
        }
      }
    }
  }, [showTable, team, fiscalQuarter, dispatch, teamHierarchy, isTeamMode, isCompanyMode]);

  // const handleTeamChange = (e) => setTeam(e.target.value);
  const handleTeamChange = (val) => {          // accept value directly from child
    setTeam(Array.isArray(val) ? val : [val]); // normalize
  };

  const handleFiscalQuarterChange = (e) => setFiscalQuarter(e.target.value);
  const handleContinue = () => {
    if ((isTeamMode && fiscalQuarter) || (team && fiscalQuarter)) setShowTable(true);
  };

  const filteredReps = Array.isArray(quotaReps)
    ? quotaReps.filter(rep => {
      if (!search) return true;
      const searchLower = search.toLowerCase();
      if (isTeamMode) {
        return rep.team && rep.team.toLowerCase().includes(searchLower);
      }
      if (isCompanyMode) {
        return rep.fiscalQuarter && rep.fiscalQuarter.toLowerCase().includes(searchLower);
      }
      return (
        (rep.fullName && rep.fullName.toLowerCase().includes(searchLower)) ||
        (rep.email && rep.email.toLowerCase().includes(searchLower))
      );
    })
    : [];

  const handleRowClick = async (rep) => {
    setSelectedRep(rep);
    if (isCompanyMode) {
      setCompanyDeleteOpen(true);
      setDeleteError(null);
      setDeleteSuccess(false);
      return;
    }
    setSelectedQuarters([]);
    setModalOpen(true);
    setDeleteError(null);
    setDeleteSuccess(false);
    setRepQuotaDetails([]);
    // Fetch quota details for the selected rep/team (for modal)
    try {
      if (isTeamMode) {
        await dispatch(
          getQuotaTeamsDrilldownThunk({ team: rep.team, fiscalQuarter }) // Drilldown
        ).unwrap();
      } else {
        await dispatch(
          removeQuotaForRepsQuarterInfo({ team: rep.team, userId: rep.userId, fiscalQuarter: fiscalQuarter })
        ).unwrap();
      }
      dispatch({ type: "info_message_generic", payload: { type: "info", message: "Quota Details Loading" } })
    } catch (err) {
      // Optionally handle error
    }
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setCompanyDeleteOpen(false);
    setSelectedRep(null);
    setSelectedQuarters([]);
    setDeleteError(null);
    setDeleteSuccess(false);
    dispatch(clearQuotaRemovalModalInfo())
  };

  const handleQuarterToggle = (newSelection) => {
    setSelectedQuarters(newSelection);
  };

  const handleDeleteQuota = async () => {
    console.log('handleDeleteQuota')

    setDeleteLoading(true);
    setDeleteError(null);

    try {
      if (isTeamMode) {
        await dispatch(
          deleteQuotaTeamThunk({
            team: selectedRep.team,
            fiscalQuarters: selectedQuarters.join(',')
          })
        ).unwrap();
        // Refresh list for team mode
        dispatch(getQuotaTeamsThunk({ fiscalQuarter }));
      } else if (isCompanyMode) {
        await dispatch(
          deleteQuotaCompanyThunk({
            fiscalQuarter: selectedRep.fiscalQuarter
          })
        ).unwrap();
        // Refresh list for company mode
        dispatch(getQuotaCompanyThunk({}));
      } else {
        await dispatch(
          deleteQuotaRemoval({
            team: selectedRep.team,
            fiscalQuarters: selectedQuarters.join(','),
            userId: selectedRep.userId,
          }, { showSuccessToast: true })
        ).unwrap();
        // Refresh list for rep mode
        dispatch(removeQuotaForReps({ team: selectedRep.team, fiscalQuarter }));
      }
      setDeleteSuccess(true);
      handleModalClose();
    } catch (err) {
      setDeleteError('Failed to delete quota. Please try again.');
    } finally {
      setDeleteLoading(false);
    }
  };

  if (!showTable && !isCompanyMode) {
    return (
      <Paper elevation={2} style={{ width: isTeamMode ? '1000px' : '1000px', padding: '2rem' }}>
        <RepFilterPanel
          team={team}
          fiscalQuarter={fiscalQuarter}
          teamOptions={teamOptions}
          teamHierarchy={teamHierarchy}
          fiscalQuarterOptions={fiscalQuarterOptions}
          onTeamChange={handleTeamChange}
          onFiscalQuarterChange={handleFiscalQuarterChange}
          onContinue={handleContinue}
          classes={classes}
          isTeamMode={isTeamMode}
        />
      </Paper>
    );
  }
  console.log('teamDebug', team)
  return (
    <Paper elevation={2} style={{ width: isTeamMode ? '1000px' : isCompanyMode ? '1000px' : '1000px' }}>
      <Box p={3} style={{ maxWidth: isTeamMode ? 1000 : isCompanyMode ? 1000 : 1000 }}>
        <Typography variant="h2" className={classes.quotaRemovalHeader} gutterBottom>
          {isTeamMode ? 'Team Quota Removal' : isCompanyMode ? 'Company Quota Removal' : 'Quota Removal'}
        </Typography>
        <Typography variant="body1" className={classes.quotaRemovalSub}>
          Select a {isTeamMode ? 'Team' : isCompanyMode ? 'Fiscal Quarter' : 'Rep'} to view {isTeamMode || isCompanyMode ? '' : 'their'} quota details and proceed with the deletion.
        </Typography>
        <RepTable
          filteredReps={filteredReps}
          team={team}
          fiscalQuarter={fiscalQuarter}
          teamOptions={teamOptions}
          fiscalQuarterOptions={fiscalQuarterOptions}
          search={search}
          onSearchChange={setSearch}
          onRowClick={handleRowClick}
          classes={classes}
          onBack={() => setShowTable(false)}
          isTeamMode={isTeamMode}
          isCompanyMode={isCompanyMode}
        />
        <QuotaDetailsModal
          open={modalOpen}
          onClose={handleModalClose}
          selectedRep={selectedRep}
          repQuotaDetails={quotaRemovalModalInfo || []}
          selectedQuarters={selectedQuarters}
          onQuarterToggle={handleQuarterToggle}
          deleteLoading={deleteLoading}
          deleteSuccess={deleteSuccess}
          deleteError={deleteError}
          onDeleteQuota={handleDeleteQuota}
          classes={classes}
          isTeamMode={isTeamMode}
        />
        <Dialog open={companyDeleteOpen} onClose={handleModalClose}>
          <DialogTitle>Confirm Deletion</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete the company quota for <strong>{selectedRep?.fiscalQuarter}</strong>?
            </Typography>
            {deleteError && (
              <Typography color="error" style={{ marginTop: 8 }}>
                {deleteError}
              </Typography>
            )}
          </DialogContent>
          <DialogActions style={{ justifyContent: 'center' }}>
            <Button onClick={handleModalClose} color="primary" variant="contained">
              Cancel
            </Button>
            <Button onClick={handleDeleteQuota} color="primary" variant="contained" disabled={deleteLoading}>
              {deleteLoading ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Paper>
  );
};

export default QuotaRemoval;
